import type { SkillAttachmentChanges } from './skill-attachment-editor';
import type { SavedSkillAttachment, SkillDocumentItem } from './skill-file-tree';
import { formatSkillTreeItemId, parseSkillTreeItemId } from './skill-tree-ids';
import type { SkillTreeItem } from './skill-tree-ids';

/**
 * Renaming and moving a file or folder in the skill's file tree.
 *
 * Both come down to the same thing: an item gets a new path (and a file maybe a new name),
 * and everything inside a folder follows it. Renaming, dragging and the Move menu all go
 * through here, so a rule fixed for one applies to the others.
 */

export type RelocatableSkillTreeItem = Extract<
  SkillTreeItem,
  { kind: 'folder' | 'saved' | 'text' | 'pdf' | 'snapshot' }
>;

export interface SkillTreeRelocation {
  item: RelocatableSkillTreeItem;
  /** The file name shown in the tree after the change. Ignored for folders. */
  name: string;
  /** The item's full path after the change. */
  path: string;
}

/**
 * Where an item is, to find it again after a save. A save moves an uploaded PDF to `<id>.pdf`
 * and gives a document copy a new path, so those also keep the id that survives the save.
 */
export interface PendingTreeTarget {
  isFolder: boolean;
  path: string;
  documentId?: string;
  pdfId?: string;
}

/**
 * Label for a document copy until the save names it after the document.
 * It is a stand-in for the row, so a move leaves the copy's name unset and the
 * save can use the document's real name.
 */
export const PENDING_DOCUMENT_COPY_LABEL = 'Adding document…';

export function getIsRelocatableSkillTreeItem(
  item: SkillTreeItem
): item is RelocatableSkillTreeItem {
  return (
    item.kind === 'folder' ||
    item.kind === 'saved' ||
    item.kind === 'text' ||
    item.kind === 'pdf' ||
    item.kind === 'snapshot'
  );
}

/** A path after the relocation: moved along when it is the folder or sits inside it. */
export function relocateSkillPath(value: string, { item, path }: SkillTreeRelocation): string {
  if (item.kind !== 'folder') return value;
  return followMovedSkillPath({ from: item.path, path: value, to: path });
}

/**
 * The same tree item after the relocation. Folders and unsaved text files are identified by
 * their path, so they get a new identity; saved files keep the path they were saved under,
 * and PDFs and document copies are identified by id.
 */
export function relocateSkillTreeItem(
  target: SkillTreeItem,
  relocation: SkillTreeRelocation
): SkillTreeItem {
  if (target.kind === 'folder')
    return { kind: 'folder', path: relocateSkillPath(target.path, relocation) };
  if (target.kind !== 'text') return target;
  const { item, path } = relocation;
  if (item.kind === 'text' && item.path === target.path) return { kind: 'text', path };
  return { kind: 'text', path: relocateSkillPath(target.path, relocation) };
}

/** `relocateSkillTreeItem` for a tree view id. An id the editor did not create is kept. */
export function relocateSkillTreeItemId(id: string, relocation: SkillTreeRelocation): string {
  const target = parseSkillTreeItemId(id);
  return target ? formatSkillTreeItemId(relocateSkillTreeItem(target, relocation)) : id;
}

/**
 * The pending attachment changes after the relocation. Saved files get a rename entry
 * (keyed by the path they were saved under); unsaved ones change in place. Returns
 * `changes` itself when no file is affected, so moving or renaming an empty folder does not
 * count as an unsaved change.
 */
export function relocateSkillAttachmentChanges({
  attachments,
  changes,
  relocation,
}: {
  attachments: SavedSkillAttachment[];
  changes: SkillAttachmentChanges;
  relocation: SkillTreeRelocation;
}): SkillAttachmentChanges {
  const { item, name, path } = relocation;
  const parent = getParentPath(path);
  const movedFolder = item.kind === 'folder' ? item.path : null;
  const getIsMoved = (folderPath: string) =>
    movedFolder !== null && getIsSkillPathInside(folderPath, movedFolder);
  let affected = false;

  const renames = [...(changes.attachmentRenames ?? [])];
  for (const file of attachments) {
    const retained =
      !changes.retainedAttachmentPaths || changes.retainedAttachmentPaths.includes(file.path);
    const index = renames.findIndex(change => change.path === file.path);
    const existing = renames[index];
    const currentPath = existing?.newPath ?? file.path;
    const isItem = item.kind === 'saved' && item.path === file.path;
    const isInFolder = movedFolder !== null && currentPath.startsWith(`${movedFolder}/`);
    if (retained && (isItem || isInFolder)) {
      affected = true;
      const change = {
        name: isItem ? name : (existing?.name ?? file.name),
        newPath: isItem ? path : relocateSkillPath(currentPath, relocation),
        path: file.path,
      };
      if (index === -1) renames.push(change);
      else renames[index] = change;
    }
  }

  const textFiles = changes.textFiles?.map(file => {
    const isItem = item.kind === 'text' && item.path === file.path;
    const newPath = isItem ? path : relocateSkillPath(file.path, relocation);
    if (newPath === file.path) return file;
    affected = true;
    return { ...file, path: newPath };
  });
  const relocatePendingFile = <TFile extends { folderPath?: string | null; name?: string | null }>(
    file: TFile,
    isItem: boolean
  ): TFile => {
    const folderPath = file.folderPath ?? '';
    if (!isItem && !getIsMoved(folderPath)) return file;
    affected = true;
    if (isItem) {
      /**
       * A move of a copy that has no name yet passes the tree's "Adding document…"
       * label. Leaving `name` unset lets the save use the document's real name.
       * A rename still supplies the name the author typed.
       */
      if (!file.name?.trim() && name === PENDING_DOCUMENT_COPY_LABEL)
        return { ...file, folderPath: parent };
      return { ...file, folderPath: parent, name };
    }
    return { ...file, folderPath: relocateSkillPath(folderPath, relocation) };
  };
  const pdfAttachments = changes.pdfAttachments?.map(file =>
    relocatePendingFile(file, item.kind === 'pdf' && item.id === file.id)
  );
  const documentSnapshots = changes.documentSnapshots?.map(file =>
    relocatePendingFile(file, item.kind === 'snapshot' && item.documentId === file.documentId)
  );

  if (!affected) return changes;
  return {
    ...changes,
    ...(renames.length ? { attachmentRenames: renames } : {}),
    ...(documentSnapshots ? { documentSnapshots } : {}),
    ...(pdfAttachments ? { pdfAttachments } : {}),
    ...(textFiles ? { textFiles } : {}),
  };
}

/**
 * Where `path` is after the item at `from` moved to `to`: the item itself, or anything inside
 * it when it is a folder. Other paths stay.
 */
export function followMovedSkillPath({
  from,
  path,
  to,
}: {
  from: string;
  path: string;
  to: string;
}): string {
  return getIsSkillPathInside(path, from) ? to + path.slice(from.length) : path;
}

/** The target for an item the tree shows, or null for a file it does not. */
export function getPendingTreeTarget({
  documentItems,
  item,
}: {
  documentItems: SkillDocumentItem[];
  item: RelocatableSkillTreeItem;
}): PendingTreeTarget | null {
  if (item.kind === 'folder') return { isFolder: true, path: item.path };
  const itemId = formatSkillTreeItemId(item);
  const path = documentItems.find(file => file.id === itemId)?.path;
  if (!path) return null;
  switch (item.kind) {
    case 'pdf':
      return { isFolder: false, path, pdfId: item.id };
    case 'snapshot':
      return { documentId: item.documentId, isFolder: false, path };
    case 'saved':
    case 'text':
      return { isFolder: false, path };
  }
}

/**
 * The item a target names now: still pending, or the saved file it became. A PDF is found by
 * its id and a document copy by its source document; anything else by its path.
 */
export function findPendingTreeItem({
  attachments,
  documentItems,
  target,
}: {
  attachments: SavedSkillAttachment[];
  documentItems: SkillDocumentItem[];
  target: PendingTreeTarget;
}): RelocatableSkillTreeItem | undefined {
  if (target.isFolder) return { kind: 'folder', path: target.path };
  const { documentId, pdfId } = target;
  if (pdfId)
    return documentItems.find(
      ({ item }) =>
        (item.kind === 'pdf' && item.id === pdfId) ||
        (item.kind === 'saved' && item.path.split('/').at(-1) === `${pdfId}.pdf`)
    )?.item;
  if (documentId) {
    const savedPath = attachments.find(file => file.sourceDocumentId === documentId)?.path;
    return documentItems.find(
      ({ item }) =>
        (item.kind === 'snapshot' && item.documentId === documentId) ||
        (item.kind === 'saved' && item.path === savedPath)
    )?.item;
  }
  return documentItems.find(file => file.path === target.path)?.item;
}

/** The folder a path sits in, '' for the skill root. */
export function getParentPath(path: string): string {
  const index = path.lastIndexOf('/');
  return index === -1 ? '' : path.slice(0, index);
}

/** `name` inside `folder`, '' for the skill root. */
export function joinSkillPath(folder: string, name: string): string {
  return folder ? `${folder}/${name}` : name;
}

/** Whether `path` is `folder` itself or sits anywhere inside it. */
export function getIsSkillPathInside(path: string, folder: string): boolean {
  return path === folder || path.startsWith(`${folder}/`);
}
